import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { useRouter } from "next/router";
import { useStore } from "outstated";
import { AppStore } from "pages/_app.js";
import dayjs from "dayjs";
// mui
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";

import Divider from "@mui/material/Divider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import useMediaQuery from "@mui/material/useMediaQuery";

// lib
import usePrevious from "components/Calculators/components/utils/usePrevious";
import loadCalculation from "components/Calculators/components/utils/loadCalculation";
import formatMoney from "lib/formatMoney";
import scroller from "lib/scroller";
import sleep from "lib/sleep";
// components
import QuestionRenderer from "components/Calculators/components/QuestionRenderer";
import SpreadLTVTable from "components/Calculators/DSCRCalculator/components/SpreadLTVTable";
import CalculatorQuestions from "components/Calculators/DSCRCalculator/components/CalculatorQuestions";
import getDSCRLTVTiers from "components/Loans/utils/getDSCRLTVTiers";
import Authenticator from "components/Authenticator";
import createCalc from "components/Calculators/DSCRCalculator/utils/createCalc";
import updateCalc from "components/Calculators/DSCRCalculator/utils/updateCalc";
import * as gtag from "components/GTagManager";
import dynamic from "next/dynamic";
const AddressAutocomplete = dynamic(() =>
  import("components/AddressAutocomplete")
);
import SaveSharePDF from "components/Calculators/components/SaveSharePDF";

export default function DSCRCalculator({
  URL,
  CapitalData,
  externalCalc,
  externalShowMeMyRate,
  plugInTheNumbers,
  setCalcs,
  externalExampleCalc,
  calcs,
  loadCalcs,
  refCalcs,
  calculatorMode, // 'DSCR' || 'DSCR loan'
}) {
  const theme = useTheme();
  const router = useRouter();
  // const  = useMediaQuery(theme.breakpoints.down("md"));

  const calcType = calculatorMode;

  const { isMobile, user, userProfile, isNewUser, setSnackbarData } =
    useStore(AppStore);

  const [loanQuoteData, setLoanQuoteData] = useState({});

  const [externalCaclObjData, setExternalCalcObjData] = useState(
    externalExampleCalc && Object.keys(externalExampleCalc).length > 0
      ? externalExampleCalc
      : {
          "Credit score": 681,
          "HOA dues": 211,
          Insurance: 1254,
          "Purchase price": 143000,
          "Monthly rent": 1450,
          "Property taxes": 988,
        }
  ); // this is default data for dscr-loan page where this custom obj isn't provided

  const [totalLoanLeft, setTotalLoanLeft] = useState(0);
  const [showQuestionDescription, setShowQuestionDescription] = useState({});
  // Add originalValueRef to track original values
  const originalValueRef = useRef(
    externalExampleCalc && Object.keys(externalExampleCalc).length > 0
      ? {
          "Credit score": externalExampleCalc.creditScore,
          "HOA dues": externalExampleCalc.hoa,
          Insurance: externalExampleCalc.insurance,
          "Purchase price": externalExampleCalc.price,
          "Monthly rent": externalExampleCalc.rent,
          "Property taxes": externalExampleCalc.taxes,
        }
      : {
          "Credit score": 681,
          "HOA dues": 211,
          Insurance: 1254,
          "Purchase price": 143000,
          "Monthly rent": 1450,
          "Property taxes": 988,
        }
  );
  // Add valueChanges state to track value changes
  const [valueChanges, setValueChanges] = useState(
    externalExampleCalc && Object.keys(externalExampleCalc).length > 0
      ? {
          "Credit score": externalExampleCalc.creditScore,
          "HOA dues": externalExampleCalc.hoa,
          Insurance: externalExampleCalc.insurance,
          "Purchase price": externalExampleCalc.price,
          "Monthly rent": externalExampleCalc.rent,
          "Property taxes": externalExampleCalc.taxes,
        }
      : {
          "Credit score": 681,
          "HOA dues": 211,
          Insurance: 1254,
          "Purchase price": 143000,
          "Monthly rent": 1450,
          "Property taxes": 988,
        }
  );
  // Answers is what users give and shown in the UI
  const [answers, setAnswers] = useState([]);
  // Questions is what has blueprint of what we ask
  const [questions, setQuestions] = useState([]);
  // All current session answers are held in this
  const [currentValues, setCurrentValues] = useState({});
  const [loanPurpose, setLoanPurpose] = useState({});
  const [loanAmount, setLoanAmount] = useState({});
  const [monthlyPmt, setMonthlyPmt] = useState({});
  const [monthlyRent, setMonthlyRent] = useState(0);
  const [annualRent, setAnnualRent] = useState(0);
  const [annualTaxes, setAnnualTaxes] = useState(0);
  const [annualInsurance, setAnnualInsurance] = useState(0);
  const [annualHOA, setAnnualHOA] = useState(0);
  const [propertyValue, setPropertyValue] = useState(0);
  const [localInterestRate, setLocalInterestRate] = useState(0);
  const [LTV, setLTV] = useState(0);
  const [annualPropertyManagementExpense, setAnnualPropertyManagementExpense] =
    useState(0);
  const [dscr, setDSCR] = useState(0);
  const [cashToClose, setCashToClose] = useState(0);
  const [cashToCloseIsPositive, setCashToCloseIsPositive] = useState(0);
  const [variableExpenseCashToClose, setVariableExpenseCashToClose] =
    useState(0);
  const [monthlyCashflow, setMonthlyCashflow] = useState(0);
  const [loanFees, setLoanFees] = useState(0);
  const [titleFees, setTitleFees] = useState(0);
  const [escrow, setEscrow] = useState(0);
  const [prepaidInterest, setPrepaidInterest] = useState(0);
  const [prepaidInsurance, setPrepaidInsurance] = useState(0);
  const [originationFee, setOriginationFee] = useState(0);
  const [estimatedAsIsValue, setEstimatedAsIsValue] = useState(0);
  const [existingDebt, setExistingDebt] = useState(0);
  const [showSaveFields, setShowSaveFields] = useState(false);
  const [changedKey, setChangedKey] = useState("");
  const [showUpdateFields, setShowUpdateFields] = useState(false);
  const [suggestionChoice, setSuggestionChoice] = useState(false);
  const [localAddress, setLocalAddress] = useState(false);
  const [calc, setCalc] = useState(externalCalc ? externalCalc : null);
  const [showCreatedSuccessMessage, setShowCreatedSuccessMessage] =
    useState(false);
  // const [showSaved, setShowSaved] = useState(false);
  const [showMeMyRate, setShowMeMyRate] = useState(false);
  // DSCR
  // const [DSCRRateRange, setDSCRRateRange] = useState([]);
  const [current5yrTreasury, setCurrent5yrTreasury] = useState(null);
  const [personalizedIntSpread, setPersonalizedIntSpread] = useState(null);
  const [DSCRCutoff, setDSCRCutoff] = useState(null);
  const [creditScoreCutoff, setCreditScoreCutoff] = useState(null);
  const [creditScore, setCreditScore] = useState(null);
  const [rateUsed, setRateUsed] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showCopiedSuccess, setShowCopiedSuccess] = useState(false);
  const [anchorEl, setAnchorEl] = useState({});
  const [isInView, setIsInView] = useState(false);
  // Add ref to track if expansion has been triggered
  const hasExpandedRef = useRef(false);
  // Add state for tracking all descriptions visibility
  const [showAllDescriptions, setShowAllDescriptions] = useState(false);
  // State for closing costs collapse
  const [showClosingCostsCollapse, setShowClosingCostsCollapse] =
    useState(false);

  // clean up if loan purpose is changed
  // everything other then below keys
  let preserveKeys = [
    "Monthly rent",
    "Annual taxes",
    "Annual insurance",
    "Annual HOA",
    "Loan purpose",
    "Calculation name",
    "Address (optional)",
  ];
  // special section that isn't part of original questions
  // but must be added later so we can still capture in 'answers'
  const firstPartOfQuestions = [
    "Monthly rent",
    "Annual taxes",
    "Annual insurance",
    "Annual HOA",
    "Loan purpose",
    "Purchase price",
    "Estimated AS-IS value",
    "Existing debt",
    "Loan amount",
    "Loan to value (LTV)",
    "Interest rate",
  ];

  const secondPartOfQuestions = [
    "Origination fee",
    "Loan fees",
    "Title fees",
    "Escrow",
    "Prepaid interest",
    "Prepaid insurance",
  ];

  const thirdPart = ["Calculation name", "Address (optional)"];

  const showMeMyRatePart = ["Credit score"];

  const handleOpen = (event, calc) => {
    const updatedAnchorEl = Object.assign({}, anchorEl);
    updatedAnchorEl[calc.id] = event.currentTarget;
    setAnchorEl(updatedAnchorEl);
  };

  const handleClose = (calc) => {
    if (calc) {
      const updatedAnchorEl = Object.assign({}, anchorEl);
      delete updatedAnchorEl[calc.id];
      setAnchorEl(updatedAnchorEl);
    }
  };

  // TODO review necessity
  const defaultValues = {
    //   "Loan purpose": "purchase",
  };

  useEffect(() => {
    loadCalculation(
      defaultValues,
      externalCalc,
      externalExampleCalc,
      CalculatorQuestions,
      setQuestions,
      setAnswers,
      setCurrentValues,
      setShowUpdateFields
    );
  }, []);

  useEffect(() => {
    if (changedKey === "Loan purpose") {
      // need to reset all subsequent questions
      // resetting current answers to 0, will force textField to show answers instead of currentValues
      const updatedCurrentValues = Object.assign({}, currentValues);

      Object.keys(updatedCurrentValues).forEach((key) => {
        if (preserveKeys.indexOf(key) === -1) {
          updatedCurrentValues[key] = undefined;
        }
      });

      setCurrentValues(updatedCurrentValues);

      setChangedKey("");
    } else {
      const creditScore = answers?.filter((answer) => {
        return answer?.q_title === "Credit score";
      })?.[0]?.a_string;
      setCreditScore(creditScore);

      const loanPurpose = answers?.filter((answer) => {
        return answer?.q_title === "Loan purpose";
      })?.[0]?.a_string;

      let propertyValue;
      if (loanPurpose === "Purchase") {
        const purchasePrice = answers.filter((answer) => {
          return answer.q_title === "Purchase price";
        })[0]?.a_string;
        propertyValue = purchasePrice;
      }

      if (loanPurpose === "Refinance") {
        const estimatedAsIsValue = answers.filter((answer) => {
          return answer.q_title === "Estimated AS-IS value";
        })[0]?.a_string;
        propertyValue = estimatedAsIsValue;
      }

      setPropertyValue(propertyValue);

      let localInterestRate;
      const interestRate = answers?.filter((answer) => {
        return answer?.q_title === "Interest rate";
      })?.[0]?.a_string;

      if (interestRate) {
        localInterestRate = interestRate;
      } else {
        localInterestRate = currentValues["Interest rate"];
      }
      setLocalInterestRate(localInterestRate);

      let loanAmount = answers?.filter((answer) => {
        return answer?.q_title === "Loan amount";
      })?.[0]?.a_string;

      let LTV = 0;
      if (loanAmount) {
        LTV = ((loanAmount / propertyValue) * 100).toFixed(2);
      } else {
        LTV = answers?.filter((answer) => {
          return answer?.q_title === "Loan to value (LTV)";
        })?.[0]?.a_string;
      }

      if (!loanAmount && LTV) {
        loanAmount = propertyValue * (LTV / 100);
      }

      const monthlyRent =
        answers?.filter((answer) => {
          return answer?.q_title === "Monthly rent";
        })?.[0]?.a_string || 0;
      const annualRent = monthlyRent * 12;

      const annualTaxes =
        answers?.filter((answer) => {
          return answer?.q_title === "Annual taxes";
        })?.[0]?.a_string || 0;
      const annualInsurance =
        answers?.filter((answer) => {
          return answer?.q_title === "Annual insurance";
        })?.[0]?.a_string || 0;
      const annualHOA =
        answers?.filter((answer) => {
          return answer?.q_title === "Annual HOA";
        })?.[0]?.a_string || 0;

      const annualPropertyManagementExpense =
        answers?.filter((answer) => {
          return answer?.q_title === "Annual property management expense";
        })?.[0]?.a_string || 0;

      // fee calcs
      let loanFees =
        answers?.filter((answer) => {
          return answer?.q_title === "Loan fees";
        })?.[0]?.a_string || 0;

      if (!loanFees) {
        loanFees = 1995;
      }

      let originationFee =
        answers?.filter((answer) => {
          return answer?.q_title === "Origination fee";
        })?.[0]?.a_string || 0;

      if (!originationFee) {
        originationFee =
          parseInt(loanAmount) < 200000 ? 2000 : parseInt(loanAmount) * 0.01;
      }

      let titleFees =
        answers?.filter((answer) => {
          return answer?.q_title === "Title fees";
        })?.[0]?.a_string || 0;

      if (!titleFees) {
        // set caculated fees and put them into the answers
        titleFees = parseInt(loanAmount) * 0.01;
      }

      let escrow = answers?.filter((answer) => {
        return answer?.q_title === "Escrow";
      })?.[0]?.a_string;

      if (!escrow) {
        escrow =
          (parseInt(annualTaxes) * 3) / 12 +
          (parseInt(annualInsurance) * 3) / 12;
      }

      let prepaidInterest =
        answers?.filter((answer) => {
          return answer?.q_title === "Prepaid interest";
        })?.[0]?.a_string || 0;

      if (!prepaidInterest) {
        const today = dayjs();
        // Get the end of the current month
        const endOfMonth = today.endOf("month");
        // Calculate the difference in days between today and the end of the month
        const daysUntilEndOfMonth = endOfMonth.diff(today, "day");

        prepaidInterest =
          ((loanAmount * (parseFloat(localInterestRate) / 100)) / 365) *
          daysUntilEndOfMonth;
      }

      let prepaidInsurance =
        answers?.filter((answer) => {
          return answer?.q_title === "Prepaid insurance";
        })?.[0]?.a_string || 0;

      if (!prepaidInsurance) {
        prepaidInsurance =
          answers?.filter((answer) => {
            return answer?.q_title === "Annual insurance";
          })?.[0]?.a_string || 0;
      }

      const estimatedAsIsValue =
        answers?.filter((answer) => {
          return answer?.q_title === "Estimated AS-IS value";
        })?.[0]?.a_string || 0;
      const existingDebt =
        answers?.filter((answer) => {
          return answer?.q_title === "Existing debt";
        })?.[0]?.a_string || 0;

      setMonthlyRent(monthlyRent);
      setAnnualRent(annualRent);
      setAnnualTaxes(annualTaxes);
      setAnnualInsurance(annualInsurance);
      setAnnualHOA(annualHOA);

      setAnnualPropertyManagementExpense(annualPropertyManagementExpense);
      setLoanFees(loanFees);
      setEstimatedAsIsValue(estimatedAsIsValue);
      setExistingDebt(existingDebt);
      setLoanPurpose(loanPurpose);
      setLoanAmount(loanAmount);
      setLTV(LTV);
      setTitleFees(titleFees);
      setEscrow(escrow);
      setPrepaidInterest(prepaidInterest);
      setPrepaidInsurance(prepaidInsurance);
      setOriginationFee(originationFee);

      const numPmt = 12;
      const numYrs = 30;

      const monthlyPmt =
        (loanAmount *
          (parseFloat(localInterestRate) / 100 / numPmt) *
          (1 + parseFloat(localInterestRate) / 100 / numPmt) **
            (numYrs * numPmt)) /
        ((1 + parseFloat(localInterestRate) / 100 / numPmt) **
          (numYrs * numPmt) -
          1);

      setMonthlyPmt(monthlyPmt);

      // Calc DSCR
      // (Rent - Utilities - Maintenance - Property Management) ÷ (Mortgage Pay + Taxes + Insurance + HOA)
      const dscr =
        parseInt(annualRent) /
        (parseInt(monthlyPmt * 12) +
          parseInt(annualTaxes) +
          parseInt(annualInsurance) +
          parseInt(annualHOA));

      if (dscr) {
        setDSCR(dscr);
      }

      let cashToClose;
      let variableExpenseCashToClose = 0;
      if (loanPurpose === "Purchase") {
        variableExpenseCashToClose = propertyValue;
      } else {
        variableExpenseCashToClose = existingDebt;
      }
      setVariableExpenseCashToClose(variableExpenseCashToClose);

      cashToClose =
        parseInt(variableExpenseCashToClose) -
        parseInt(loanAmount) +
        parseInt(originationFee) +
        parseInt(loanFees) +
        parseInt(titleFees) +
        parseInt(escrow) +
        parseInt(prepaidInterest) +
        parseInt(prepaidInsurance);

      setCashToClose(cashToClose);
      setCashToCloseIsPositive(cashToClose > 0);

      const monthlyCashflow =
        parseInt(monthlyRent) -
        (parseInt(annualTaxes) / 12 +
          parseInt(annualInsurance) / 12 +
          parseInt(monthlyPmt) +
          parseInt(annualHOA) / 12);

      if (monthlyCashflow) {
        setMonthlyCashflow(monthlyCashflow);
      }

      // update current values to trigger pre-filled calculations
      const updatedCurrentValues = Object.assign({}, currentValues);

      updatedCurrentValues["Loan fees"] = loanFees;
      updatedCurrentValues["Escrow"] = escrow;
      updatedCurrentValues["Title fees"] = titleFees;
      updatedCurrentValues["Prepaid interest"] = prepaidInterest;
      updatedCurrentValues["Prepaid insurance"] = prepaidInsurance;

      updatedCurrentValues["Origination fee"] = originationFee;

      setCurrentValues(updatedCurrentValues);
    }
  }, [answers, totalLoanLeft]);

  // Save the previous state
  const previousValues = usePrevious(currentValues);

  useEffect(() => {
    for (const key in currentValues) {
      if (
        currentValues[key] !== previousValues[key] &&
        currentValues[key] !== undefined
      ) {
        // If the value of the key has changed, set the changed key state
        let changedKey = key;
        // break; // Exit loop since we found the changed key

        if (changedKey === "Loan purpose") {
          const updatedAnswers = answers.slice(0);

          updatedAnswers.forEach((answer) => {
            if (!preserveKeys.includes(answer.q_title)) {
              answer.a_string = "";
            }
          });

          setAnswers(updatedAnswers);

          // this will be picked up in useEffect
          // for answers to reset currentValues
          setChangedKey(changedKey);
        }

        // if property value changes, we need to update LTV
        let tKey = "";
        if (loanPurpose === "Purchase") {
          tKey = "Purchase price";
        } else {
          tKey = "Estimated AS-IS value";
        }

        if (changedKey === tKey) {
          const updatedLTV = (
            (loanAmount / currentValues[changedKey]) *
            100
          ).toFixed(2);

          const updatedAnswers = answers.slice(0);

          const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
            return answer?.q_title === "Loan to value (LTV)";
          });

          toUpdateAnswer.a_string = updatedLTV;

          setAnswers(updatedAnswers);
          // resetting current answers to 0, will force textField to show answers instead of currentValues
          const updatedCurrentValues = Object.assign({}, currentValues);
          updatedCurrentValues["Loan to value (LTV)"] = undefined;

          setCurrentValues(updatedCurrentValues);
        }

        if (changedKey === "Loan amount") {
          const updatedLoanAmount = (
            (currentValues[changedKey] / propertyValue) *
            100
          ).toFixed(2);

          const updatedAnswers = answers.slice(0);

          const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
            return answer?.q_title === "Loan to value (LTV)";
          });

          toUpdateAnswer.a_string = updatedLoanAmount;

          setAnswers(updatedAnswers);
          // resetting current answers to 0, will force textField to show answers instead of currentValues
          const updatedCurrentValues = Object.assign({}, currentValues);
          updatedCurrentValues["Loan to value (LTV)"] = undefined;

          setCurrentValues(updatedCurrentValues);
        }

        if (changedKey === "Loan to value (LTV)") {
          const updatedLoanAmount =
            propertyValue * (currentValues[changedKey] / 100);

          const updatedAnswers = answers.slice(0);

          const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
            return answer?.q_title === "Loan amount";
          });

          toUpdateAnswer.a_string = updatedLoanAmount;

          setAnswers(updatedAnswers);
          // resetting current answers to 0, will force textField to show answers instead of currentValues
          const updatedCurrentValues = Object.assign({}, currentValues);
          updatedCurrentValues["Loan amount"] = undefined;

          setCurrentValues(updatedCurrentValues);
        }
      }
    }
  }, [currentValues]);

  useEffect(() => {
    if (loanQuoteData && Object.keys(loanQuoteData).length > 0) {
      setCurrent5yrTreasury(parseFloat(loanQuoteData?.treasury5yr?.rate));

      const ltv = answers?.filter((answer) => {
        return answer?.q_title === "Loan to value (LTV)";
      })?.[0]?.a_string;

      // find closes integer key in the LTVObj
      // however ltv might not be an exact number that matches one of the keys
      // then use that to get the personalized interest rate spread
      const ltvKeys = Object.keys(loanQuoteData?.LTVObj);

      ltvKeys.forEach((key, index) => {
        if (
          parseInt(ltv) >= parseInt(key) &&
          ltvKeys[index + 1] &&
          parseInt(ltv) <= parseInt(ltvKeys[index + 1])
        ) {
          setPersonalizedIntSpread(loanQuoteData?.LTVObj[key]?.creditSpread);
          setDSCRCutoff(loanQuoteData?.LTVObj[key]?.DSCRCutoff);
          setCreditScoreCutoff(loanQuoteData?.LTVObj[key]?.creditScoreCutoff);
        }
      });

      if (plugInTheNumbers) {
        const updatedAnswers = answers.slice(0);

        const topLTV = ltvKeys[ltvKeys.length - 1];
        const ltvRange = loanQuoteData?.LTVObj[topLTV];

        const intRate = (
          parseFloat(loanQuoteData.treasury5yr.rate) +
          parseFloat(ltvRange.creditSpread)
        ).toFixed(2);

        const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
          return answer?.q_title === "Loan to value (LTV)";
        });

        toUpdateAnswer.a_string = topLTV;

        const [toUpdateAnswerInterestRate] = updatedAnswers.filter((answer) => {
          return answer?.q_title === "Interest rate";
        });

        toUpdateAnswerInterestRate.a_string = intRate;

        // loanFees
        // purchase price
        externalCaclObjData.loanAmount = externalCaclObjData.price * 0.7;
        externalCaclObjData.loanFees = externalCaclObjData.loanAmount * 0.03;

        // plug in Cacl example
        const plugInCalcAnswers = [
          {
            q_id: "0",
            a_type: "mc",
            a_error: false,
            q_title: "Loan purpose",
            a_string: "Purchase",
            node_type: "node",
            a_input_type: "",
            q_invalid_answer_help_text: "",
          },
          {
            q_id: "00",
            a_type: "currency",
            a_error: false,
            q_title: "Purchase price",
            a_string: externalCaclObjData.price,
            node_type: "node",
            a_input_type: "number",
            q_invalid_answer_help_text: "Please enter a dollar amount above 0",
          },
          {
            q_id: "00000",
            a_type: "currency",
            a_error: false,
            q_title: "Loan fees",
            a_string: externalCaclObjData.loanFees,
            node_type: "node",
            a_input_type: "number",
            q_invalid_answer_help_text: "Please enter a value above 0",
          },
          {
            q_id: "000000",
            a_type: "currency",
            a_error: false,
            q_title: "Monthly rent",
            a_string: externalCaclObjData.rent,
            node_type: "node",
            a_input_type: "number",
            q_invalid_answer_help_text: "Please enter a value above 0",
          },
          {
            q_id: "0000000",
            a_type: "currency",
            a_error: false,
            q_title: "Annual taxes",
            a_string: externalCaclObjData.taxes,
            node_type: "node",
            a_input_type: "number",
            q_invalid_answer_help_text: "Please enter a value above 0",
          },
          {
            q_id: "00000000",
            a_type: "currency",
            a_error: false,
            q_title: "Annual insurance",
            a_string: externalCaclObjData.insurance,
            node_type: "node",
            a_input_type: "number",
            q_invalid_answer_help_text: "Please enter a value above 0",
          },
          {
            q_id: "000000000",
            a_type: "currency",
            a_error: false,
            q_title: "Annual HOA",
            a_string: externalCaclObjData.hoa,
            node_type: "node",
            a_input_type: "number",
            q_invalid_answer_help_text: "Please enter a value above 0",
          },
          {
            q_id: "0000000000",
            a_type: "text",
            a_error: false,
            q_title: "Calculation name",
            a_string: "Plug in example",
            node_type: "node",
            a_input_type: "text",
            q_invalid_answer_help_text: "",
          },
          {
            q_id: "00000000000",
            a_type: "address",
            a_error: false,
            q_title: "Address (optional)",
            a_string: "",
            node_type: "node",
            q_invalid_answer_help_text: "Please enter or select an address",
          },
          {
            q_id: "0000000000000",
            a_type: "mc",
            a_error: false,
            q_title: "Refinance type",
            a_string: "Cash out refinance",
            node_type: "leaf",
            a_input_type: "",
            q_invalid_answer_help_text: "",
          },
          {
            q_id: "00000",
            q_title: "Loan amount",
            a_string: externalCaclObjData.loanAmount,
            node_type: "node",
            a_type: "currency",
            a_input_type: "number",
          },
          {
            q_id: "00000000",
            q_title: "Loan to value (LTV)",
            a_string:
              (externalCaclObjData.loanAmount / externalCaclObjData.price) *
              100,
            node_type: "node",
            a_type: "percent",
            a_input_type: "number",
            q_invalid_answer_help_text:
              "Please enter a value between 50 and 90 (a common range of offered LTVs)",
            a_error: false,
          },
        ];

        plugInCalcAnswers.forEach((answer) => {
          const [toUpdate] = updatedAnswers.filter((updatedAnswer) => {
            return updatedAnswer.q_title === answer.q_title;
          });
          // toUpdate
          if (toUpdate) {
            toUpdate.a_string = answer.a_string;
          }
        });

        setAnswers(updatedAnswers);
        // setCalc(plugInCalc);
        // setShowUpdateFields(true);
        setShowSaveFields(true);
      }
    }
  }, [loanQuoteData]);

  useEffect(() => {
    if (externalShowMeMyRate !== false && externalShowMeMyRate) {
      setShowMeMyRate(externalShowMeMyRate);
    }

    if (externalShowMeMyRate === false) {
      setShowMeMyRate(externalShowMeMyRate);
    }
  }, [externalShowMeMyRate]);

  useEffect(() => {
    if (plugInTheNumbers) {
      const updatedAnswers = answers.slice(0);

      const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
        return answer?.q_title === "Loan purpose";
      });

      if (!toUpdateAnswer.a_string) {
        // just default to purchase for ease for now
        toUpdateAnswer.a_string = "Purchase";
      }

      // Credit score from example harcoded at 681
      const [toUpdateAnswerCreditScore] = updatedAnswers.filter((answer) => {
        return answer?.q_title === "Credit score";
      });

      toUpdateAnswerCreditScore.a_string = externalCaclObjData.creditScore;

      setAnswers(updatedAnswers);

      // now get the tiers, no longer need LTV, will just show all LTVs even if its set
      // to show wider range of options
      (async () => {
        await getDSCRLTVTiers(updatedAnswers, setLoanQuoteData);
      })();
    }
  }, [plugInTheNumbers]);

  const handleUserSelection = (
    value,
    answer,
    answers,
    questions,
    setAnswers
  ) => {
    const questionsSlice = questions.slice();
    const updatedAnswers = answers.slice(0);
    const isValidAnswer = answer.q_validation_func(value, answers);
    const valueChange = valueChanges[answer.q_title];

    if (isValidAnswer) {
      const targetAnswer = updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0];

      // Calculate value change for all field types
      if (
        answer.a_type === "currency" ||
        answer.a_type === "number" ||
        answer.a_type === "percent" ||
        answer.a_type === "text"
      ) {
        // Store original value when user starts typing, but only if not already set
        if (!originalValueRef.current[answer.q_title]) {
          // Check if we have a default value from externalExampleCalc or default values
          const defaultValue = valueChanges[answer.q_title];
          originalValueRef.current[answer.q_title] =
            defaultValue || currentValues[answer.q_title] || "";
        }

        let newValue = value;
        let oldValue = originalValueRef.current[answer.q_title];

        // Convert to numbers for numeric types
        if (
          answer.a_type === "currency" ||
          answer.a_type === "number" ||
          answer.a_type === "percent"
        ) {
          newValue = parseFloat(value) || 0;
          oldValue = parseFloat(oldValue) || 0;
        }

        const difference =
          answer.a_type === "text"
            ? newValue !== oldValue
            : newValue - oldValue;

        if (difference !== 0 && difference !== false) {
          setValueChanges((prev) => ({
            ...prev,
            [answer.q_title]: difference,
          }));

          // Clear the change and original value after 2 seconds
          setTimeout(() => {
            setValueChanges((prev) => {
              const newChanges = { ...prev };
              delete newChanges[answer.q_title];
              return newChanges;
            });
            delete originalValueRef.current[answer.q_title];
          }, 2000);
        }
      }

      // save users answer
      targetAnswer.a_string = value;
      // reset error just in case
      targetAnswer.a_error = false;

      // determine if need to insert follow up questions
      const next_q_id = answer.q_next_step_func(answer, answers, questions);

      const q_ids = updatedAnswers.map((answer) => {
        return answer.q_id;
      });

      // NEXT QUESTION is always next in position
      // NOT like before when it was based on length of q_id
      // this was limiting in terms of folding questions of the tree back
      // and allowing the tree to merge back to left most path
      const posOfNextQuestion =
        updatedAnswers
          .map((updatedAnswer) => {
            return updatedAnswer.q_id;
          })
          .indexOf(answer.q_id) + 1;

      const nextPotentialQuestion = updatedAnswers[posOfNextQuestion];

      if (
        next_q_id &&
        nextPotentialQuestion &&
        nextPotentialQuestion?.q_id !== next_q_id
      ) {
        // OFF LEFT MOST PATH
        const newNextQuestion = questionsSlice.filter((question) => {
          return question.q_id === next_q_id;
        })[0];

        // LEAF, remove all subsequent questions from answers
        if (newNextQuestion.node_type === "leaf") {
          // if a LEAF never delete anything (like using slice)
          // instead add hidden property
          for (let i = posOfNextQuestion; i < updatedAnswers.length; i++) {
            updatedAnswers[i].node_visibility = "hidden";
          }
        } else {
          // NODE, make sure subsequent questions are prents
          // need to make left most path starting from next questions's id.
          let leftMostPathFromCurrentNode = [];
          let deeperNextQuestion = newNextQuestion;
          let deeperNextQuestionChildrenLength =
            deeperNextQuestion.q_child_nodes.length;
          const map_q_id = questionsSlice.map((question) => {
            return question.q_id;
          });

          while (deeperNextQuestionChildrenLength !== 0) {
            deeperNextQuestion =
              questions[
                map_q_id.indexOf(
                  deeperNextQuestion.q_next_step_func(
                    deeperNextQuestion,
                    [],
                    []
                  )
                )
              ];

            if (deeperNextQuestion) {
              deeperNextQuestionChildrenLength =
                deeperNextQuestion.q_child_nodes.length;

              leftMostPathFromCurrentNode.push(deeperNextQuestion);
            } else {
              deeperNextQuestionChildrenLength = 0;
            }
          }

          const earlierQuestions = updatedAnswers.slice(
            0,
            posOfNextQuestion + 2
          );

          let counter = 0;
          for (
            let i = posOfNextQuestion + 1;
            i < leftMostPathFromCurrentNode.length + posOfNextQuestion + 1;
            i++
          ) {
            if (
              updatedAnswers[i]?.q_id === leftMostPathFromCurrentNode[counter]
            ) {
              //show the node
              // updatedAnswers[i].node_type = "node";
            } else {
              //or overwrite if different
              updatedAnswers[i] = leftMostPathFromCurrentNode[counter];
              // updatedAnswers[i].node_type = "node";
            }
            counter = counter + 1;
          }

          // make sure all nodes are re-updated
          // to have node_type be node so nothing is hidden
          // if people jump around
          for (let i = 0; i < updatedAnswers.length; i++) {
            if (updatedAnswers[i].node_type === "node") {
              updatedAnswers[i].node_visibility = "visible";
            }
          }
        }

        updatedAnswers[posOfNextQuestion] = questionsSlice.filter(
          (question) => {
            return question.q_id === next_q_id;
          }
        )[0];
        updatedAnswers[posOfNextQuestion].node_visibility = "visible";
      }

      // UNIQUE TO DSCR CALCULATOR
      // set refinance type to be always cash out
      // updatedAnswers
      const [toUpdateAnswer] = updatedAnswers.filter((answer) => {
        return answer?.q_title === "Refinance type";
      });

      toUpdateAnswer.a_string = "Cash out refinance";

      setAnswers(updatedAnswers);
    } else {
      updatedAnswers.filter((updatedAnswer) => {
        return updatedAnswer.q_id === answer.q_id;
      })[0].a_error = true;

      setAnswers(updatedAnswers);
      return;
    }
  };

  const generateAnswerOptions = (answer, index, answers) => {
    const questionsSlice = questions.slice();

    let answerOptionsUI;

    switch (answer.a_type) {
      case "leaf":
        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`} sx={{ mb: 2 }}>
            <div></div>
          </Box>
        );
        break;
      case "percent":
        let localValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : !isNaN(currentValues[answer.q_title])
            ? currentValues[answer.q_title]
            : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              margin: theme.spacing(1),
              width: "100%",
              [theme.breakpoints.down("md")]: {
                padding: `0 !important`,
              },
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{
                width: "100%",
                [theme.breakpoints.down("md")]: {
                  padding: `0 !important`,
                },
              }}
              error={answer.a_error}
              InputLabelProps={{ shrink: localValue }}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              value={localValue || ""}
              onChange={(e) => {
                const num = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;

                setCurrentValues(updatedCurrentValues);

                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();

                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );

                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );

                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );

                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
          </Box>
        );
        break;
      case "mc":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              flex: "0 1 100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {answer.a_opts(answers).map((a_opt, index) => {
              {
                return (
                  <Button
                    id={
                      index === 0
                        ? `text_field_${answer.q_title}_${
                            calc?.id ? calc.id : 0
                          }`
                        : `text_field_${answer.q_title}_opt_${index}`
                    }
                    sx={{ margin: theme.spacing(1) }}
                    variant={
                      answer.a_string === a_opt ? "contained" : "outlined"
                    }
                    onClick={(e) => {
                      const updatedCurrentValues = Object.assign(
                        {},
                        currentValues
                      );
                      updatedCurrentValues[answer.q_title] = a_opt;

                      setCurrentValues(updatedCurrentValues);

                      handleUserSelection(
                        a_opt,
                        answer,
                        answers,
                        questions,
                        setAnswers
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        const updatedCurrentValues = Object.assign(
                          {},
                          currentValues
                        );
                        updatedCurrentValues[answer.q_title] = a_opt;

                        setCurrentValues(updatedCurrentValues);

                        handleUserSelection(
                          a_opt,
                          answer,
                          answers,
                          questions,
                          setAnswers
                        );

                        const next_q_id = answer.q_next_step_func(
                          answer,
                          answers,
                          questions
                        );

                        if (next_q_id) {
                          const nextQuestion = questions.find(
                            (q) => q.q_id === next_q_id
                          );

                          const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                          const nextQuestionTextField = document.getElementById(
                            nextQuestionTextFieldId
                          );

                          if (nextQuestionTextField) {
                            nextQuestionTextField.focus();
                          }
                        }
                      }
                    }}
                  >
                    {a_opt}
                  </Button>
                );
              }
            })}
          </Box>
        );
        break;
      case "text":
        const localTextValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? answer.a_string
              : null
            : currentValues[answer.q_title];

        answerOptionsUI = (
          <Box key={`${index}_${answer.a_type}`}>
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              sx={{ margin: theme.spacing(5, 0) }}
              error={answer.a_error}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_input_type}
              value={localTextValue}
              InputLabelProps={{ shrink: localTextValue }}
              onChange={(e) => {
                const val = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = val;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  val,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              helperText={
                answer.a_error ? answer.q_invalid_answer_help_text : ""
              }
            />
          </Box>
        );
        break;
      case "currency":
        const uiValue =
          currentValues[answer.q_title] === undefined
            ? answer.a_string
              ? `$${formatMoney(answer.a_string, 0)}`
              : null
            : !isNaN(currentValues[answer.q_title])
            ? `$${formatMoney(currentValues[answer.q_title], 0)}`
            : null;

        const valueChange = valueChanges[answer.q_title];
        const changeDisplay = valueChange ? (
          <Typography
            sx={{
              position: "absolute",
              right: theme.spacing(2),
              top: "50%",
              transform: "translateY(-50%)",
              color: valueChange > 0 ? "success.main" : "error.main",
              animation: "fadeOut 2s forwards",
              "@keyframes fadeOut": {
                "0%": { opacity: 1 },
                "70%": { opacity: 1 },
                "100%": { opacity: 0 },
              },
              zIndex: 1,
              pointerEvents: "none",
            }}
          >
            {valueChange > 0 ? "+" : "-"}$
            {formatMoney(Math.abs(valueChange), 0)}
          </Typography>
        ) : null;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(3),
              width: "100%",
              position: "relative",
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={answer.q_help_text ? answer.q_help_text : ""}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  paddingRight: valueChange
                    ? theme.spacing(8)
                    : theme.spacing(2),
                },
              }}
              label={
                answer.a_error
                  ? "Error"
                  : answer.q_placeholder
                  ? answer.q_placeholder
                  : answer.q_title
              }
              type={answer.a_type_input}
              inputProps={{
                inputMode: "numeric",
              }}
              value={uiValue || ""}
              onChange={(e) => {
                const num = e.target.value.replace(/\D/g, "");
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = num;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  num,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  const next_q_id = answer.q_next_step_func(
                    answer,
                    answers,
                    questions
                  );
                  if (next_q_id) {
                    const nextQuestion = questions.find(
                      (q) => q.q_id === next_q_id
                    );
                    const nextQuestionTextFieldId = `text_field_${nextQuestion.q_title}_${calc.id}`;
                    const nextQuestionTextField = document.getElementById(
                      nextQuestionTextFieldId
                    );
                    if (nextQuestionTextField) {
                      nextQuestionTextField.focus();
                    }
                  }
                }
              }}
            />
            {changeDisplay}
          </Box>
        );
        break;
      case "number":
        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: theme.spacing(1),
              fontSize: theme.spacing(2),
            }}
          >
            <TextField
              id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
              helperText={
                answer.a_error
                  ? answer.q_invalid_answer_help_text
                  : answer.q_help_text
                  ? answer.q_help_text
                  : ""
              }
              error={answer.a_error}
              label={answer.a_error ? "Error" : answer.q_title}
              type={answer.a_type_input}
              value={
                currentValues[answer.q_title] === undefined
                  ? answer.a_string
                    ? answer.a_string
                    : null
                  : currentValues[answer.q_title]
              }
              InputLabelProps={{
                shrink:
                  currentValues[answer.q_title] === undefined
                    ? answer.a_string
                      ? answer.a_string
                      : null
                    : currentValues[answer.q_title],
              }}
              onChange={(e) => {
                const value = e.target.value;
                const updatedCurrentValues = Object.assign({}, currentValues);
                updatedCurrentValues[answer.q_title] = value;
                setCurrentValues(updatedCurrentValues);
                handleUserSelection(
                  value,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );

                // this is done to reset
                // 'get rate' output when new stuff is typed in
                setLoanQuoteData({});
              }}
            />
          </Box>
        );
        break;
      case "address":
        const address = calc?.Address;

        answerOptionsUI = (
          <Box
            key={`${index}_${answer.a_type}`}
            id={`text_field_${answer.q_title}_${calc?.id ? calc.id : 0}`}
            sx={{ margin: theme.spacing(3, 0, 3, 0) }}
          >
            <AddressAutocomplete
              autocompleteOnKeyDown={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                if (e.key === "Enter") {
                  setSuggestionChoice(place.formatted_address);

                  handleUserSelection(
                    place.formatted_address,
                    answer,
                    answers,
                    questions,
                    setAnswers
                  );
                }
              }}
              autocompleteOnClick={(
                e,
                setSuggestionsVisible,
                setLocalAddress,
                place,
                locationList,
                setLocationList
              ) => {
                setSuggestionChoice(place.formatted_address);

                handleUserSelection(
                  place.formatted_address,
                  answer,
                  answers,
                  questions,
                  setAnswers
                );
              }}
              textFieldLabel={answer.a_error ? "Error" : answer.q_title}
              textFieldAriaLabel={"Enter address of the property for this loan"}
              searchOptionsTypes={[]}
              externalLocalAddress={address !== "" && address}
              error={answer.a_error}
            />
          </Box>
        );
        break;

      default:
    }

    return answerOptionsUI;
  };

  const showError =
    loanQuoteData?.LTVObj &&
    (Object.keys(loanQuoteData.LTVObj).length === 0 ||
      creditScore <
        loanQuoteData.LTVObj[Object.keys(loanQuoteData.LTVObj)[0]]
          .creditScoreCutoff);

  // Update the scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (!hasExpandedRef.current && calculatorMode === "DSCR" && !isMobile) {
        const dscrSection = document.getElementById("pdf-5");
        if (dscrSection) {
          const rect = dscrSection.getBoundingClientRect();
          const isLiftedOffBottom = rect.bottom < window.innerHeight;

          if (isLiftedOffBottom) {
            setShowQuestionDescription((prev) => ({
              ...prev,
              DSCR: true,
            }));
            hasExpandedRef.current = true; // Mark as expanded
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [calculatorMode]); // Add calculatorMode to dependencies

  const resetCalculator = () => {
    // Loop through all answers and set a_string to ""
    const updatedAnswers = answers.map((answer) => ({
      ...answer,
      a_string: "",
    }));

    // Reset current values to ""
    const updatedCurrentValues = Object.keys(currentValues).reduce(
      (acc, key) => {
        acc[key] = ""; // Set each current value to an empty string
        return acc;
      },
      {}
    );

    setCurrentValues(updatedCurrentValues); // Update current values in state
    setAnswers(updatedAnswers); // Update answers in state
  };

  const toggleAllDescriptions = () => {
    const newState = !showAllDescriptions;
    setShowAllDescriptions(newState);

    // Get all question titles from the answers
    const allQuestionTitles = answers.map((answer) => answer.q_title);

    // Create updated state with all questions set to the new state
    const updatedShowQuestionDescription = allQuestionTitles.reduce(
      (acc, title) => {
        acc[title] = newState;
        return acc;
      },
      {} // Start with empty object
    );

    // Also include the special sections
    // UPDATE: dont include actually
    updatedShowQuestionDescription["Monthly payment"] = newState;
    updatedShowQuestionDescription["Total loan"] = newState;
    // updatedShowQuestionDescription["DSCR"] = newState;
    // updatedShowQuestionDescription["Cash flow and cash to close"] = newState;
    // updatedShowQuestionDescription["Monthly cashflow"] = newState;
    // updatedShowQuestionDescription["Cash to close"] = newState;

    setShowQuestionDescription(updatedShowQuestionDescription);
  };

  // Set initial state based on calculatorMode
  useEffect(() => {
    if (calculatorMode === "DSCR loan") {
      setShowQuestionDescription((prev) => ({
        ...prev,
        // "Cash flow and cash to close": true,
        // "Monthly cashflow": true,
        // "Cash to close": true,
      }));
    }
  }, [calculatorMode]);

  // Removed the faulty scroll listener useEffect

  // Add useEffect to manage exclusive showing of descriptions
  useEffect(() => {
    const descriptionsToManage = ["DSCR", "Monthly cashflow", "Cash to close"];
    const activeDescription = descriptionsToManage.find(
      (key) => showQuestionDescription[key]
    );

    if (activeDescription) {
      const updatedShowQuestionDescription = { ...showQuestionDescription };

      descriptionsToManage.forEach((key) => {
        if (key !== activeDescription) {
          updatedShowQuestionDescription[key] = false;
        }
      });

      if (
        JSON.stringify(updatedShowQuestionDescription) !==
        JSON.stringify(showQuestionDescription)
      ) {
        setShowQuestionDescription(updatedShowQuestionDescription);
      }
    }
  }, [
    showQuestionDescription["DSCR"],
    showQuestionDescription["Monthly cashflow"],
    showQuestionDescription["Cash to close"],
  ]);

  return (
    <Box
      key={`calculation_${externalCalc.id ? externalCalc.id : 0}`}
      sx={{
        display: "flex",
        flexDirection: "column",
        margin: theme.spacing(0, "auto"),
        width: "100%",
      }}
    >
      {router.pathname.includes("/my/calculation") && calc && (
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            sx={{
              margin: theme.spacing("auto", 0.5, "auto", "auto"),
            }}
            key={`icon_button_delete_calc_${calc.id}`}
            aria-label="loan settings"
          >
            <MoreVertIcon
              key={`vert_icon_delete_calc_${calc.id}`}
              aria-controls={
                Boolean(anchorEl[calc.id]) ? "basic-menu" : undefined
              }
              aria-haspopup="true"
              aria-expanded={Boolean(anchorEl[calc.id]) ? "true" : undefined}
              onClick={(e) => handleOpen(e, calc)}
            />
            <Menu
              key={`menu_delete_calc_${calc.id}`}
              anchorEl={anchorEl[calc.id]}
              open={Boolean(anchorEl[calc.id])}
              onClose={() => handleClose(calc)}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
            >
              <MenuItem key={`menu_item_delete_${calc.id}`}>
                <Button
                  sx={{ width: "100%" }}
                  variant="contained"
                  color="error"
                  onClick={() => {
                    (async () => {
                      await updateCalc(user, calc, {
                        Deleted: true,
                      });

                      handleClose(calc);

                      await sleep(1000); // BE deleted flag need a sec to return on next call
                      await loadCalcs(refCalcs);
                    })();
                  }}
                >
                  Delete calculation
                </Button>
              </MenuItem>
            </Menu>
          </IconButton>
        </Box>
      )}

      {/* Add Reset and Info buttons */}
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={resetCalculator}
          sx={{
            width: "25%",
            margin: theme.spacing(1),
            [theme.breakpoints.down("md")]: {
              width: "33%",
            },
            "@media print": { display: "none" },
          }}
          className="no-print-element"
        >
          Reset
        </Button>
        <IconButton
          onClick={toggleAllDescriptions}
          color="info"
          sx={{
            "@media print": { display: "none" },
          }}
          className="no-print-element"
        >
          {showAllDescriptions ? <KeyboardArrowUpIcon /> : <InfoOutlinedIcon />}
        </IconButton>
      </Box>

      {router.pathname.includes("/my/calculation") &&
        loanPurpose &&
        cashToClose &&
        monthlyCashflow && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                margin: theme.spacing(0, "auto", 6, "auto"),
                [theme.breakpoints.down("md")]: {
                  width: "auto",
                  margin: theme.spacing(1, 1, 4, 1),
                  flexDirection: "column",
                  display: "flex",
                },
              }}
            >
              <Box
                sx={{
                  width: "30%",
                  display: "flex",
                  flexDirection: "column",
                  margin: theme.spacing(0, "auto", 6, "auto"),
                  [theme.breakpoints.down("md")]: {
                    width: "auto",
                    margin: theme.spacing(1, 1, 4, 1),
                  },
                }}
              >
                {loanPurpose && (
                  <Typography
                    sx={{
                      margin: theme.spacing(1),
                      fontSize: theme.spacing(2.5),
                      textAlign: "center",
                    }}
                    variant="body1"
                    color="text.primary"
                  >
                    {`${loanPurpose} - $${formatMoney(
                      loanAmount,
                      0
                    )} - ${localInterestRate}%`}
                  </Typography>
                )}

                {monthlyCashflow && (
                  <Typography
                    sx={{
                      fontSize: theme.spacing(3),
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    variant="body1"
                    color="text.primary"
                  >
                    Monthly cashflow:{" "}
                    <Typography
                      sx={{
                        color:
                          monthlyCashflow > 0
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        fontSize: theme.spacing(3),
                      }}
                      variant="span"
                    >
                      {" "}
                      {monthlyCashflow > 0 && "+"}
                      {`$${formatMoney(monthlyCashflow, 0)}`}
                    </Typography>
                  </Typography>
                )}
                {cashToClose && (
                  <Typography
                    sx={{
                      fontSize: theme.spacing(2.25),
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    variant="body1"
                    color="text.primary"
                  >
                    {cashToClose < 0 ? "Cash proceeds" : "Cash to close"}:
                    <Typography
                      sx={{
                        color:
                          cashToClose > 0
                            ? theme.palette.primary.main
                            : theme.palette.success.main,
                        fontSize: theme.spacing(2.25),
                      }}
                      variant="span"
                    >
                      {cashToClose < 0 && " +"}
                      {` $${formatMoney(Math.abs(cashToClose), 0)}`}
                    </Typography>
                  </Typography>
                )}
                {cashToClose > 0 && monthlyCashflow && (
                  <Typography
                    sx={{
                      fontSize: theme.spacing(2.25),
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                    variant="body1"
                    color="text.primary"
                  >
                    {"Years to recoup"}:
                    <Typography
                      sx={{
                        color: theme.palette.success.main,
                        fontSize: theme.spacing(2.25),
                      }}
                      variant="span"
                    >
                      {(cashToClose / monthlyCashflow / 12).toFixed(2)}
                    </Typography>
                  </Typography>
                )}
              </Box>
              <Divider />
            </Box>
          </>
        )}

      {answers.length > 0 ? (
        <Box
          id="calculator-to-print"
          sx={{
            margin: theme.spacing(0, 0, 0, 0),
            borderRadius: theme.spacing(1),
            [theme.breakpoints.down("md")]: {
              padding: theme.spacing(1),
            },
          }}
        >
          <Box id="pdf-4">
            <QuestionRenderer
              answers={answers}
              questions={firstPartOfQuestions}
              theme={theme}
              showQuestionDescription={showQuestionDescription}
              setShowQuestionDescription={setShowQuestionDescription}
              generateAnswerOptions={generateAnswerOptions}
              currentValues={currentValues}
            />
          </Box>
          <Box
            sx={{
              margin: theme.spacing(1, 0, 0, 0),
              [theme.breakpoints.down("md")]: {
                margin: theme.spacing(0, 0, 0, 0),
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Box
                  sx={{
                    flex: "0 1 50%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <ButtonBase
                    sx={{
                      width: "100%",
                      padding: theme.spacing(1),
                      "&:focus": {
                        outline: "none",
                        borderRadius: theme.spacing(0.5),
                        boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                          theme.palette.info.main
                        }`,
                      },
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1),
                        padding: 0,
                      },
                    }}
                    onClick={() => {
                      const updatedShowQuestionDescription = {
                        ...showQuestionDescription,
                        "Monthly payment":
                          !showQuestionDescription["Monthly payment"],
                      };
                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: theme.spacing(3),
                        textAlign: "left",
                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(2),
                        },
                      }}
                    >
                      Monthly payment
                    </Typography>
                    {showQuestionDescription["Monthly payment"] ? (
                      <KeyboardArrowUpIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                      />
                    ) : (
                      <InfoOutlinedIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                        color="info"
                      />
                    )}
                  </ButtonBase>
                </Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    textAlign: "center",
                    flex: "0 1 50%",
                    margin: "auto",
                  }}
                >
                  ${formatMoney(monthlyPmt, 0)} /month
                </Box>
              </Box>
              <Collapse in={showQuestionDescription["Monthly payment"]}>
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      margin: theme.spacing(1),
                      fontSize: theme.spacing(1.8),
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1, 0, 1, 1),
                      },
                    }}
                  >
                    Monthly payment that will cover loan's principal and
                    interest (P & I)
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </Box>
          <Box
            sx={{
              margin: theme.spacing(2, 0, 0, 0),
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Box
                  sx={{
                    flex: "0 1 50%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <ButtonBase
                    sx={{
                      width: "100%",
                      padding: theme.spacing(1),
                      "&:focus": {
                        outline: "none",
                        borderRadius: theme.spacing(0.5),
                        boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                          theme.palette.info.main
                        }`,
                      },
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1),
                        padding: 0,
                      },
                    }}
                    onClick={() => {
                      const updatedShowQuestionDescription = {
                        ...showQuestionDescription,
                        "Total loan": !showQuestionDescription["Total loan"],
                      };
                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: theme.spacing(3),
                        textAlign: "left",
                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(2),
                        },
                      }}
                    >
                      Total loan
                    </Typography>
                    {showQuestionDescription["Total loan"] ? (
                      <KeyboardArrowUpIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                      />
                    ) : (
                      <InfoOutlinedIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                        color="info"
                      />
                    )}
                  </ButtonBase>
                </Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    textAlign: "center",
                    flex: "0 1 50%",
                    margin: "auto",
                  }}
                >
                  ${formatMoney(loanAmount, 0)}
                </Box>
              </Box>
              <Collapse in={showQuestionDescription["Total loan"]}>
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      margin: theme.spacing(1),
                      fontSize: theme.spacing(1.8),
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1, 0, 1, 1),
                      },
                    }}
                  >
                    The total amount of the loan
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          </Box>
          <Box
            id="pdf-5"
            sx={{
              position: isInView ? "relative" : "sticky",
              minHeight: {
                xs: theme.spacing(16),
                md: isInView ? "auto" : theme.spacing(14),
              },
              bottom: 0,
              width: "100%",
              backgroundColor: theme.palette.background.paper,
              borderTop: !isInView
                ? `1px solid ${theme.palette.grey[300]}`
                : "none",
              transition: "all 0.3s ease",
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                marginTop: theme.spacing(2),
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                }}
              >
                <Box
                  sx={{
                    flex: "0 1 50%",
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <ButtonBase
                    sx={{
                      width: "100%",
                      padding: theme.spacing(1),
                      "&:focus": {
                        outline: "none",
                        borderRadius: theme.spacing(0.5),
                        boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                          theme.palette.info.main
                        }`,
                      },
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(0.5),
                        padding: 0,
                      },
                    }}
                    onClick={() => {
                      const updatedShowQuestionDescription = {
                        ...showQuestionDescription,
                        DSCR: !showQuestionDescription["DSCR"],
                        "Monthly cashflow": false,
                        "Cash to close": false,
                      };

                      setShowQuestionDescription(
                        updatedShowQuestionDescription
                      );
                    }}
                  >
                    <Typography
                      variant="h2"
                      sx={{
                        fontWeight: 600,
                        fontSize: theme.spacing(3.5),
                        textAlign: "left",

                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(2),
                        },
                      }}
                    >
                      DSCR
                    </Typography>
                    {showQuestionDescription["DSCR"] ? (
                      <KeyboardArrowUpIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                      />
                    ) : (
                      <InfoOutlinedIcon
                        sx={{
                          display: "flex",
                          margin: theme.spacing("auto", 1, "auto", "auto"),
                          fontSize: theme.spacing(2.5),
                        }}
                        color="info"
                      />
                    )}
                  </ButtonBase>
                </Box>
                <Box
                  sx={{
                    [theme.breakpoints.down("md")]: {
                      fontSize: theme.spacing(2),
                    },
                    fontWeight: 600,
                    fontSize: theme.spacing(3),
                    textAlign: "center",
                    flex: "0 1 50%",
                    margin: "auto",
                    color:
                      dscr.toFixed(2) > 1.09
                        ? theme.palette.success.main
                        : theme.palette.secondary.main,
                  }}
                >
                  {dscr.toFixed(2)}
                </Box>
              </Box>
              <Collapse in={showQuestionDescription["DSCR"]}>
                <Box sx={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      margin: theme.spacing(1),
                      fontSize: theme.spacing(1.8),
                      [theme.breakpoints.down("md")]: {
                        margin: theme.spacing(1, 0, 1, 1),
                      },
                    }}
                  >
                    <Box sx={{ mb: 1 }}>
                      <strong>DSCR (Debt Service Coverage Ratio)</strong> is
                      calculated as:
                    </Box>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                          fontWeight: "bold",
                        }}
                      >
                        <span>Annual rental income:</span>
                        <span>${formatMoney(annualRent, 0)}</span>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <span>Annual mortage service:</span>
                        <span>${formatMoney(monthlyPmt * 12, 0)}</span>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <span>Annual taxes:</span>
                        <span>${formatMoney(annualTaxes, 0)}</span>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <span>Annual insurance:</span>
                        <span>${formatMoney(annualInsurance, 0)}</span>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <span>Annual HOA:</span>
                        <span>${formatMoney(annualHOA, 0)}</span>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                          fontWeight: "bold",
                        }}
                      >
                        <span>Total annual debt service:</span>
                        <span>
                          $
                          {formatMoney(
                            monthlyPmt * 12 +
                              parseInt(annualTaxes) +
                              parseInt(annualInsurance) +
                              parseInt(annualHOA),
                            0
                          )}
                        </span>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 1,
                          pt: 1,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          fontWeight: "bold",
                        }}
                      >
                        <span>DSCR</span>
                        <span>
                          ${formatMoney(annualRent, 0)} ÷ $
                          {formatMoney(
                            monthlyPmt * 12 +
                              parseInt(annualTaxes) +
                              parseInt(annualInsurance) +
                              parseInt(annualHOA),
                            0
                          )}{" "}
                          ={" "}
                          <span
                            style={{
                              color:
                                dscr >= 1.1
                                  ? theme.palette.success.main
                                  : theme.palette.secondary.main,
                            }}
                          >
                            {dscr.toFixed(2)}
                          </span>
                        </span>
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      OfferMarket's minimum DSCR is 1.1. A DSCR above 1.1
                      indicates that the property generates enough income to
                      cover its debt obligations.
                    </Box>
                  </Typography>
                </Box>
              </Collapse>
            </Box>

            <Collapse
              in={
                // Always expand if in 'DSCR loan' mode, otherwise use state
                calculatorMode === "DSCR loan" ||
                showQuestionDescription[`Cash flow and cash to close`]
              }
            >
              <Box
                id="pdf-6"
                tabindex={0}
                sx={{
                  zIndex: 2,
                  "&:focus": {
                    outline: "none",
                    borderRadius: theme.spacing(0.5),
                    boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                      theme.palette.success.main
                    }`,
                  },
                }}
              >
                <Box
                  id="title_Monthly cashflow"
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(0.5),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          const updatedShowQuestionDescription = {
                            ...showQuestionDescription,
                            DSCR: false,
                            "Monthly cashflow":
                              !showQuestionDescription["Monthly cashflow"],
                            "Cash to close": false,
                          };

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: 600,
                            fontSize: theme.spacing(3.5),
                            textAlign: "left",
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(2),
                            },
                          }}
                        >
                          Monthly cashflow
                        </Typography>
                        {showQuestionDescription["Monthly cashflow"] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>

                    <Box
                      sx={{
                        [theme.breakpoints.down("md")]: {
                          fontSize: theme.spacing(2),
                        },
                        fontWeight: 600,
                        fontSize: theme.spacing(3),
                        textAlign: "center",
                        flex: "0 1 50%",
                        margin: "auto",
                        color:
                          monthlyCashflow > -1
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                      }}
                    >
                      {`$${formatMoney(monthlyCashflow, 0)}`}
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription["Monthly cashflow"]}>
                    <Box sx={{ width: "100%" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          margin: theme.spacing(1),
                          fontSize: theme.spacing(1.8),
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(1, 0, 1, 1),
                          },
                        }}
                      >
                        {`$${formatMoney(parseInt(monthlyRent), 0)}`} Monthly
                        rent
                        <br />- {`$${formatMoney(
                          parseInt(monthlyPmt),
                          0
                        )}`}{" "}
                        Monthly loan payment
                        <br />-{" "}
                        {`$${formatMoney(parseInt(annualTaxes) / 12, 0)}`}{" "}
                        Prorated taxes
                        <br />-{" "}
                        {`$${formatMoney(
                          parseInt(annualInsurance) / 12,
                          0
                        )}`}{" "}
                        Prorated insurance
                        <br />- {`$${formatMoney(
                          parseInt(annualHOA) / 12,
                          0
                        )}`}{" "}
                        Monthly HOAs (if any)
                        <br /> = {`$${formatMoney(monthlyCashflow, 0)}`}{" "}
                        Estimated monthly cashflow
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
              <Box
                id="pdf-8" // Renamed ID to avoid conflict
                tabindex={0}
                sx={{
                  zIndex: 2,
                  "&:focus": {
                    outline: "none",
                    borderRadius: theme.spacing(0.5),
                    boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                      theme.palette.success.main
                    }`,
                  },
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        flex: "0 1 50%",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                      }}
                    >
                      <ButtonBase
                        sx={{
                          width: "100%",
                          padding: theme.spacing(1),
                          "&:focus": {
                            outline: "none",
                            borderRadius: theme.spacing(0.5),
                            boxShadow: `0 0 0 ${theme.spacing(0.5)} ${
                              theme.palette.info.main
                            }`,
                          },
                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(0.5),
                            padding: 0,
                          },
                        }}
                        onClick={() => {
                          const updatedShowQuestionDescription = {
                            ...showQuestionDescription,
                            DSCR: false,
                            "Monthly cashflow": false,
                            "Cash to close":
                              !showQuestionDescription["Cash to close"],
                          };

                          setShowQuestionDescription(
                            updatedShowQuestionDescription
                          );
                        }}
                      >
                        <Typography
                          variant="h2"
                          sx={{
                            fontWeight: 600,
                            fontSize: theme.spacing(3.5),
                            textAlign: "left",
                            color: theme.palette.primary.dark,
                            [theme.breakpoints.down("md")]: {
                              fontSize: theme.spacing(2),
                              textAlign: "center",
                            },
                          }}
                        >
                          {loanPurpose === "Purchase"
                            ? "Cash to close"
                            : "Proceeds"}
                        </Typography>
                        {showQuestionDescription["Cash to close"] ? (
                          <KeyboardArrowUpIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                          />
                        ) : (
                          <InfoOutlinedIcon
                            sx={{
                              display: "flex",
                              margin: theme.spacing("auto", 1, "auto", "auto"),
                              fontSize: theme.spacing(2.5),
                            }}
                            color="info"
                          />
                        )}
                      </ButtonBase>
                    </Box>

                    <Box
                      sx={{
                        fontWeight: 600,
                        textAlign: "center",
                        flex: "0 1 50%",
                        margin: "auto",
                        fontSize: theme.spacing(3.2),
                        color:
                          loanPurpose === "Refinance"
                            ? cashToClose < 0
                              ? theme.palette.success.main
                              : theme.palette.primary.main
                            : "inherit",
                        [theme.breakpoints.down("sm")]: {
                          width: "100%",
                          fontSize: theme.spacing(2),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          margin: "auto",
                          borderRadius: theme.spacing(0.5),
                          display: "inline-block",
                        }}
                      >
                        {/* {userProfile && Object.keys(userProfile).length === 0
                          ? 99999 // this is a dummy number behind the blurr
                          : loanPurpose === "Purchase"
                          ? `$${formatMoney(cashToClose, 0)}`
                          : `$${formatMoney(-1 * cashToClose, 0)}`} */}
                        {userProfile &&
                        Object.keys(userProfile).length === 0 ? (
                          <>
                            <Button
                              color={"success"}
                              variant={"contained"}
                              // sx={{
                              //   top: theme.spacing(2),
                              //   width: "100%",
                              //   // margin: theme.spacing(1, "auto"),
                              //   fontWeight: "bold",
                              //   fontSize: "1rem",
                              //   padding: theme.spacing(1, 0),
                              //   boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                              //   borderRadius: "4px",
                              //   display: "flex",
                              //   alignItems: "center",
                              //   justifyContent: "center",
                              //   transition: "box-shadow 0.2s",
                              //   "&:hover": {
                              //     boxShadow: "0 3px 6px rgba(0, 0, 0, 0.15)",
                              //     filter: "brightness(1.05)",
                              //   },
                              //   [theme.breakpoints.down("md")]: {
                              //     padding: theme.spacing(1, 0),
                              //     width: "100%",
                              //     margin: theme.spacing(1, 0),
                              //   },
                              // }}
                              onClick={() => {
                                setShowAuth(!showAuth);
                              }}
                            >
                              Sign in
                            </Button>

                            <Dialog
                              open={showAuth}
                              onClose={() => setShowAuth(false)}
                              maxWidth="sm"
                              fullWidth
                            >
                              <DialogContent>
                                <Authenticator
                                  customTitle={"Log in or sign up to do that"}
                                />
                              </DialogContent>
                            </Dialog>
                          </>
                        ) : loanPurpose === "Purchase" ? (
                          `$${formatMoney(cashToClose, 0)}`
                        ) : (
                          `$${formatMoney(-1 * cashToClose, 0)}`
                        )}
                      </Box>
                    </Box>
                  </Box>
                  <Collapse in={showQuestionDescription["Cash to close"]}>
                    <Box
                      sx={{
                        width: "100%",
                        borderRadius: theme.spacing(1),
                        marginTop: theme.spacing(1.5),
                        padding: theme.spacing(1.5),
                        [theme.breakpoints.down("md")]: {
                          padding: theme.spacing(1),
                          marginTop: theme.spacing(1),
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          margin: theme.spacing(0),
                          fontSize: theme.spacing(1.8),

                          [theme.breakpoints.down("md")]: {
                            margin: theme.spacing(0.5),
                          },
                        }}
                      >
                        {`$${formatMoney(
                          parseInt(
                            Math.abs(
                              parseInt(variableExpenseCashToClose) -
                                parseInt(loanAmount)
                            )
                          ),
                          0
                        )}`}{" "}
                        {loanPurpose === "Refinance"
                          ? "Proceeds"
                          : "Downpayment"}
                        <br />
                        {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                        {`$${formatMoney(parseInt(originationFee), 0)}`}{" "}
                        Origination fee
                        <br />
                        {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                        {`$${formatMoney(parseInt(loanFees), 0)}`} Loan fees
                        <br />
                        {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                        {`$${formatMoney(parseInt(titleFees), 0)}`} Title fees
                        <br />
                        {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                        {`$${formatMoney(parseInt(escrow), 0)}`} Escrow
                        <br />
                        {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                        {`$${formatMoney(parseInt(prepaidInterest), 0)}`}{" "}
                        Prepaid interest
                        <br />
                        {loanPurpose === "Refinance" ? "-" : "+"}{" "}
                        {`$${formatMoney(parseInt(prepaidInsurance), 0)}`}{" "}
                        Prepaid insurance
                        <br />
                        <Box
                          component="span"
                          sx={{
                            fontWeight: 600,
                            paddingTop: theme.spacing(0.5),
                            marginTop: theme.spacing(0.5),
                            display: "inline-block",
                            filter:
                              userProfile &&
                              Object.keys(userProfile).length === 0
                                ? "blur(5px)"
                                : "blur(0)",
                            padding: theme.spacing(0.5, 1),
                            borderRadius: theme.spacing(0.5),
                          }}
                        >
                          ={" "}
                          {userProfile && Object.keys(userProfile).length === 0
                            ? `$0.00`
                            : `$${formatMoney(
                                parseInt(Math.abs(cashToClose)),
                                0
                              )}`}{" "}
                          {loanPurpose === "Refinance"
                            ? "Proceeds"
                            : "Cash to close"}
                        </Box>
                      </Typography>
                    </Box>
                  </Collapse>
                </Box>
              </Box>
            </Collapse>
          </Box>
          <Box
            sx={{
              margin: theme.spacing(2, 0, 0, 0),

              position: "relative",
              overflow: "hidden",
              [theme.breakpoints.down("md")]: {
                margin: theme.spacing(1, 0, 2, 0),
                padding: theme.spacing(2),
              },
            }}
          >
            {(showQuestionDescription[`Cash flow and cash to close`] ||
              calculatorMode === "DSCR loan") && (
              <Button
                sx={{ width: { xs: "100%", md: "auto" }, margin: "auto" }}
                variant="outlined" // Changed to outlined for consistency
                color="primary"
                fullWidth // Make button full width
                onClick={() =>
                  setShowClosingCostsCollapse(!showClosingCostsCollapse)
                }
                endIcon={
                  showClosingCostsCollapse ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )
                }
              >
                {showClosingCostsCollapse
                  ? "Hide closing costs"
                  : "Show closing costs"}
              </Button>
            )}

            <Collapse in={showClosingCostsCollapse}>
              <Box id="pdf-6.5">
                {" "}
                {/* Closing costs content */}
                <QuestionRenderer
                  answers={answers}
                  questions={secondPartOfQuestions}
                  theme={theme}
                  showQuestionDescription={showQuestionDescription}
                  setShowQuestionDescription={setShowQuestionDescription}
                  generateAnswerOptions={generateAnswerOptions}
                  currentValues={currentValues}
                />
              </Box>
            </Collapse>
          </Box>{" "}
          <Box
            sx={{
              margin: theme.spacing(0, 0, 0, 0),
              display: "flex",
              justifyContent: "center",
            }}
          >
            {calculatorMode !== "DSCR loan" && (
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  let updatedShowQuestionDescription = Object.assign(
                    {},
                    showQuestionDescription
                  );
                  const willShow =
                    !updatedShowQuestionDescription[
                      `Cash flow and cash to close`
                    ];
                  updatedShowQuestionDescription[
                    `Cash flow and cash to close`
                  ] = willShow;

                  // If showing, also expand the inner collapsibles
                  if (willShow) {
                    // updatedShowQuestionDescription["Monthly cashflow"] = true;
                    // updatedShowQuestionDescription["Cash to close"] = true;
                  }

                  setShowQuestionDescription(updatedShowQuestionDescription);
                }}
                endIcon={
                  showQuestionDescription[`Cash flow and cash to close`] ? (
                    <KeyboardArrowUpIcon />
                  ) : (
                    <KeyboardArrowDownIcon />
                  )
                }
                sx={{
                  borderRadius: theme.spacing(1),
                  margin: theme.spacing(4, 0),
                  padding: theme.spacing(1.5, 4),
                  fontWeight: 600,
                  fontSize: theme.spacing(2.2),
                  borderWidth: 2,
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  "&:hover": {
                    borderWidth: 2,
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.contrastText,
                  },
                  [theme.breakpoints.down("md")]: {
                    padding: theme.spacing(1.2, 3),
                    fontSize: theme.spacing(2),
                  },
                }}
              >
                {showQuestionDescription[`Cash flow and cash to close`]
                  ? "Hide cash flow and cash to close"
                  : "Show cash flow and cash to close"}
              </Button>
            )}
          </Box>
          <Box id="pdf-10">
            <SaveSharePDF
              answers={answers}
              allowedQuestionTitles={thirdPart}
              generateAnswerOptions={generateAnswerOptions}
              calc={calc}
              setCalc={setCalc}
              calculatorType={calcType}
            />
          </Box>
          {dscr > 1.1 && (
            <Alert
              sx={{
                margin: theme.spacing(10, 0),
                width: "100%",
                "& .MuiAlert-message": {
                  width: "100%",
                },
                "& .MuiButtonBase-root": {
                  backgroundColor: `${theme.palette.success.main}`,
                },
              }}
              severity="success"
              variant={"outlined"}
            >
              <>
                <b>Looks like a great fit for our DSCR program.</b>
                <br />
                <br />
                Speak with a DSCR loan expert when you get an instant quote
              </>

              <Box
                sx={{
                  margin: theme.spacing(1, "auto", 0, "auto"),
                  display: "flex",
                  flexDirection: "column",
                  width: "33%",
                  [theme.breakpoints.down("md")]: {
                    width: "100%",
                  },
                }}
              >
                <Button
                  color="success"
                  sx={{
                    textDecoration: "none !important",
                    color: "white !important",
                    margin: theme.spacing(1.5, 0, 0, 0),
                    "&:hover": {
                      color: `${theme.palette.primary.contrastColor} !important`,
                      backgroundColor: `${theme.palette.success.dark} !important`,
                    },
                    [theme.breakpoints.down("md")]: {
                      width: "auto",
                    },
                  }}
                  href={"/loans?ref=dscr-calculator-btn"}
                  onClick={() => {
                    gtag.event({ action: "dscr_calc_to_quoter_navigation" });
                  }}
                >
                  <Box
                    sx={{
                      margin: theme.spacing(0, 1),
                      fontSize: theme.spacing(2),
                      padding: theme.spacing(0.5),
                    }}
                  >
                    {" "}
                    ⚡
                  </Box>
                  Instant quote{" "}
                </Button>
                <Typography
                  component="h2"
                  sx={{
                    display: "flex",
                    fontSize: theme.spacing(1.5),
                    margin: theme.spacing(0, 0, 0, "auto"),
                    [theme.breakpoints.down("md")]: {
                      display: "block",
                    },
                  }}
                >
                  *takes 2 minutes, no credit pull
                </Typography>
              </Box>
            </Alert>
          )}
        </Box>
      ) : (
        <Box
          sx={{ margin: theme.spacing(2, 0), width: "100%", minHeigh: "100vh" }}
        >
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
          <Skeleton
            sx={{ width: "100%", margin: theme.spacing(2, 0) }}
            animation="wave"
            variant="rounded"
            height={theme.spacing(15)}
          />
        </Box>
      )}
    </Box>
  );
}
